import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Building2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { DocRow, DocsSummary } from "./SubcontractorDocs";
import AiInsight from "./AiInsight";

const FIELDS = [
  { key: "raison_sociale", label: "Raison sociale *", span2: true },
  { key: "siret", label: "SIRET" },
  { key: "contact", label: "Contact" },
  { key: "telephone", label: "Téléphone" },
  { key: "email", label: "Email (relances + réception des documents)" },
  { key: "adresse", label: "Adresse", span2: true },
];
const EMPTY = Object.fromEntries(FIELDS.map((f) => [f.key, ""]));

function IdentityForm({ sub, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => setForm({ ...EMPTY, ...Object.fromEntries(FIELDS.map((f) => [f.key, sub?.[f.key] || ""])) }), [sub]);

  const save = async () => {
    if (!form.raison_sociale.trim()) return toast.error("Raison sociale requise");
    setBusy(true);
    try {
      const { data } = sub?.id ? await api.patch(`/subcontractors/${sub.id}`, form) : await api.post("/subcontractors", form);
      toast.success(sub?.id ? "Sous-traitant mis à jour" : "Sous-traitant créé");
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-2 gap-3" data-testid="sub-identity-form">
      {FIELDS.map((f) => (
        <label key={f.key} className={`flex flex-col text-xs text-norea-textMuted ${f.span2 ? "col-span-2" : ""}`}>
          <span className="mb-1">{f.label}</span>
          <input value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="input-field text-sm" data-testid={`sub-input-${f.key}`} />
        </label>
      ))}
      <div className="col-span-2 flex justify-end">
        <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid="sub-save-btn">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} {sub?.id ? "Enregistrer" : "Créer le sous-traitant"}
        </button>
      </div>
    </div>
  );
}

function SubcontractorSheet({ sub, docTypes, open, onClose, onChanged }) {
  const [current, setCurrent] = useState(sub);
  useEffect(() => setCurrent(sub), [sub]);
  const update = (data) => { setCurrent(data); onChanged(); };
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="sub-sheet">
        <SheetHeader>
          <SheetTitle className="font-heading">{current?.id ? current.raison_sociale : "Nouveau sous-traitant"}</SheetTitle>
        </SheetHeader>
        <div className="mt-5 space-y-6">
          <section>
            <div className="overline mb-3">Identité de l'entreprise</div>
            <IdentityForm sub={current} onSaved={update} />
          </section>
          {current?.id && <AiInsight key={`${current.id}-${current.updated_at}`} scope="subcontractor" refId={current.id} title="Bilan de conformité IA" />}
          {current?.id ? (
            <section data-testid="sub-documents">
              <div className="overline mb-1">Documents ({current.docs_present}/{current.docs_total})</div>
              <p className="text-xs text-norea-textMuted mb-2">Un email de relance est envoyé au sous-traitant 7 jours avant l'expiration. Les PDF qu'il renvoie par email sont classés et remplacés automatiquement.</p>
              {docTypes.map((t) => <DocRow key={t.key} sub={current} type={t} onUpdated={update} />)}
            </section>
          ) : (
            <p className="text-xs text-norea-textMuted italic">Créez la fiche pour pouvoir déposer les documents.</p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function SubcontractorsPanel() {
  const [items, setItems] = useState(null);
  const [docTypes, setDocTypes] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => api.get("/subcontractors").then((r) => setItems(r.data.items || [])).catch(() => setItems([])), []);
  useEffect(() => {
    load();
    api.get("/subcontractors/doc-types").then((r) => setDocTypes(r.data.doc_types || []));
  }, [load]);

  const remove = async (s) => {
    if (!window.confirm(`Supprimer le sous-traitant « ${s.raison_sociale} » et ses documents ?`)) return;
    await api.delete(`/subcontractors/${s.id}`);
    toast.success("Sous-traitant supprimé");
    load();
  };

  return (
    <div className="card-flat p-6" data-testid="panel-subcontractors">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h3 className="font-heading text-xl font-medium text-norea-text">Sous-traitants</h3>
          <p className="text-xs text-norea-textMuted mt-1 max-w-xl">
            Entreprises de pose et leurs 7 documents obligatoires. Ils sont proposés dans le champ « Sous-traitant » des dossiers.
          </p>
        </div>
        <button onClick={() => setSelected({})} className="btn-secondary" data-testid="btn-add-subcontractor">
          <Plus className="w-4 h-4" /> Ajouter un sous-traitant
        </button>
      </div>
      {items?.length > 0 && <AiInsight key={items.map((s) => s.updated_at).join()} scope="subcontractors" title="Synthèse IA de conformité" className="mb-4" />}
      {items === null ? (
        <div className="text-sm text-norea-textMuted">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-norea-textMuted italic py-6 text-center border border-dashed border-norea-border rounded" data-testid="sub-empty">
          Aucun sous-traitant — ajoutez-en avec le bouton ci-dessus.
        </div>
      ) : (
        <div className="divide-y divide-norea-border border border-norea-border rounded">
          {items.map((s, i) => (
            <div key={s.id} className="flex items-center gap-4 px-4 py-3 hover:bg-norea-surfaceHover cursor-pointer group" onClick={() => setSelected(s)} data-testid={`sub-row-${i}`}>
              <Building2 className="w-4 h-4 text-norea-textMuted flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-norea-text truncate">{s.raison_sociale}</div>
                <div className="text-xs text-norea-textMuted truncate">{[s.contact, s.email, s.telephone].filter(Boolean).join(" · ") || "—"}</div>
              </div>
              <DocsSummary sub={s} />
              <button onClick={(e) => { e.stopPropagation(); remove(s); }} className="p-1.5 rounded text-norea-textMuted hover:text-red-600 opacity-0 group-hover:opacity-100" data-testid={`sub-delete-${i}`} aria-label="Supprimer">
                <Trash2 className="w-4 h-4" />
              </button>
              <ChevronRight className="w-4 h-4 text-norea-textDisabled" />
            </div>
          ))}
        </div>
      )}
      <SubcontractorSheet sub={selected} docTypes={docTypes} open={!!selected} onClose={() => setSelected(null)} onChanged={load} />
    </div>
  );
}
